import { useState, useMemo, useCallback, useEffect, useRef, type ChangeEvent, type FocusEvent } from 'react';
import { loanCapitalProgram, loanLtvLtc, ltvLtcText } from '@/pages/pipeline-intelligence/dealResolution';
import { chartStackSegmentColor } from '@/config/chartTheme';
import {
  Calculator,
  ChevronRight,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  Building2,
  DollarSign,
  FileText,
  Download,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useAntarcticaDeals } from '@/hooks/useDemoDataAccess';
import { reportsService } from '@/services/loans';
import { reconciliationService } from '@/services/reconciliation';
import { useDealDossierStore } from '@/store/dealDossierStore';
import { amountUnitOf, propertyValueOf, recordedNoiOf } from '@/pages/liveModuleData';
import {
  annualDebtConstant,
  programFits,
  sizingConventions,
  type ProgramFit,
} from '@/pages/screeningCreditBox';

import type { Loan, AssetClass } from '@/types';

/* ------------------------------------------------------------------ */
/*  Constants                                                          */
/* ------------------------------------------------------------------ */

const PROPERTY_TYPES: AssetClass[] = [
  'Multifamily', 'Office', 'Industrial', 'Retail',
  'Hospitality', 'Lab/R&D', 'Mixed-Use', 'Senior Living', 'Land',
];

const MARKETS = [
  'Dallas, TX', 'Miami, FL', 'New York, NY', 'Los Angeles, CA',
  'Chicago, IL', 'Atlanta, GA', 'Phoenix, AZ', 'Denver, CO',
  'Boston, MA', 'Seattle, WA', 'Austin, TX', 'Nashville, TN',
];

/**
 * The sizer works in dollars. Live loans are dollars; the curated demo book is
 * in $MM and declares it (`Loan.amountUnit`). The unit is never guessed from
 * the size — "under 1,000 means $MM" printed a live $20 loan as $20,000,000.
 */
const asDollars = (v: number | null | undefined, deal: Pick<Loan, 'amountUnit'>) =>
  typeof v === 'number' && Number.isFinite(v) ? (amountUnitOf(deal) === 'USD_MM' ? v * 1_000_000 : v) : 0;

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

const USD = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/** Deal and API amounts are stored in dollars. Keep units numeric and apply
 * the presentation format at the edge so binary floating-point tails never
 * leak into Screening. */
const formatScreeningMoney = (value: number) =>
  USD.format(Number.isFinite(value) ? value : 0);

const fmt = formatScreeningMoney;

const pct = (v: number, decimals = 1) =>
  `${(v * 100).toFixed(decimals)}%`;

function fitBadge(status: ProgramFit['status']) {
  if (status === 'Needs Input')
    return (
      <span className="inline-flex items-center gap-0.5 text-[10px] font-medium text-stone-400">
        <AlertTriangle size={10} /> Needs input
      </span>
    );
  if (status === 'In Box')
    return (
      <span className="inline-flex items-center gap-0.5 text-[10px] font-medium text-emerald-400">
        <CheckCircle2 size={10} /> In Box
      </span>
    );
  return (
    <span className="inline-flex items-center gap-0.5 text-[10px] font-medium text-red-400">
      <XCircle size={10} /> Out of Box
    </span>
  );
}

const fieldLabel = 'text-[9px] font-mono uppercase tracking-wider text-stone-500';
const sectionLabel = 'text-[9px] font-mono uppercase tracking-widest text-stone-500';

function MoneyInput({
  label,
  value,
  onValueChange,
}: {
  label: string
  value: number
  onValueChange: (value: number) => void
}) {
  const inputRef = useRef<HTMLInputElement>(null)
  const editingRef = useRef(false)
  const formattedValue = value.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })

  // Keep the display synchronized when another deal is selected, but never
  // rewrite the active DOM value while a person (or Playwright) is typing.
  // A controlled, comma-formatted value here causes rapid type/fill events to
  // race React renders and reinsert the prior formatted number mid-entry.
  useEffect(() => {
    const input = inputRef.current
    if (input && !editingRef.current) input.value = formattedValue
  }, [formattedValue])

  const parseDraft = (raw: string): number | null => {
    const normalized = raw.replace(/[^0-9.-]/g, '')
    if (!normalized || normalized === '-' || normalized === '.' || normalized === '-.') return null
    const parsed = Number(normalized)
    return Number.isFinite(parsed) ? Math.max(0, parsed) : null
  }

  const updateCalculation = (event: ChangeEvent<HTMLInputElement>) => {
    const parsed = parseDraft(event.currentTarget.value)
    if (parsed != null) onValueChange(parsed)
  }

  const commit = (event: FocusEvent<HTMLInputElement>) => {
    const parsed = parseDraft(event.currentTarget.value) ?? 0
    editingRef.current = false
    onValueChange(parsed)
    event.currentTarget.value = parsed.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
  }

  return (
    <label className="space-y-0.5">
      <span className={fieldLabel}>{label}</span>
      <span className="relative block">
        <span aria-hidden className="pointer-events-none absolute inset-y-0 left-2 flex items-center text-[11px] text-stone-400">
          $
        </span>
        <input
          ref={inputRef}
          aria-label={label}
          type="text"
          inputMode="decimal"
          className="input input-compact w-full pl-5 font-mono tabular-nums"
          defaultValue={formattedValue}
          onFocus={(event) => {
            editingRef.current = true
            event.currentTarget.select()
          }}
          onChange={updateCalculation}
          onBlur={commit}
        />
      </span>
    </label>
  )
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export default function Screening() {
  const deals = useAntarcticaDeals('screening');
  // The sizer starts EMPTY. It used to open on a made-up $70M / $4.5M Dallas
  // multifamily deal with "In Box" verdicts beside a queue of real deals (UW-08).
  const [propertyValue, setPropertyValue] = useState(0);
  const [noi, setNoi] = useState(0);
  const [propertyType, setPropertyType] = useState<AssetClass | ''>('');
  const [market, setMarket] = useState('');
  const [selectedDealId, setSelectedDealId] = useState<string | null>(null);
  const [valueSource, setValueSource] = useState<'record' | 'derived' | 'none' | null>(null);
  const [valueFormula, setValueFormula] = useState<string | null>(null);

  const queueDeals = useMemo(
    () => deals.filter((d) => d.stage === 'Triage' || d.stage === 'Post-screen Quote'),
    [deals],
  );

  const creditBox = useQuery({
    queryKey: ['credit-box'],
    queryFn: () => reconciliationService.getCreditBox(),
    staleTime: 5 * 60_000,
  });
  const rules = creditBox.data?.rules;

  const productFits: ProgramFit[] = useMemo(
    () => (rules ? programFits({ value: propertyValue, noi, assetType: propertyType }, rules) : []),
    [rules, propertyValue, noi, propertyType],
  );
  const bestFit = useMemo(
    () =>
      productFits
        .filter((f) => f.status === 'In Box')
        .sort((a, b) => b.sizedLoan - a.sizedLoan)[0] ?? null,
    [productFits],
  );

  const calc = useMemo(() => {
    const conventions = rules ? sizingConventions(rules) : { coupon: null, amortYears: null };
    const constant =
      conventions.coupon != null && conventions.amortYears != null
        ? annualDebtConstant(conventions.coupon, conventions.amortYears)
        : 0;
    const capRate = propertyValue > 0 && noi > 0 ? noi / propertyValue : null;
    const maxLoan = bestFit?.sizedLoan ?? 0;
    const maxLtvPct = propertyValue > 0 && maxLoan > 0 ? maxLoan / propertyValue : 0;
    const annualDebtService = maxLoan * constant;
    const dscrAtMax = annualDebtService > 0 && noi > 0 ? noi / annualDebtService : null;
    const debtYield = maxLoan > 0 && noi > 0 ? noi / maxLoan : null;
    return { capRate, maxLoan, maxLtvPct, dscrAtMax, debtYield, conventions };
  }, [rules, propertyValue, noi, bestFit]);

  // The structure a program fit implies: its sized loan and the equity below it.
  // No rate is invented; the Credit Box's minimum coupon is shown as a floor.
  const suggested = useMemo(() => {
    if (!bestFit || !(propertyValue > 0)) return null;
    const senior = bestFit.sizedLoan;
    const equity = Math.max(propertyValue - senior, 0);
    const coupon = calc.conventions.coupon;
    return {
      layers: [
        { label: 'Senior', amount: senior, rate: coupon != null ? `min coupon ${pct(coupon, 2)}` : '', pct: senior / propertyValue },
        { label: 'Equity', amount: equity, rate: '', pct: equity / propertyValue },
      ],
      product: bestFit.name,
    };
  }, [bestFit, propertyValue, calc.conventions.coupon]);

  const populateFromDeal = useCallback((deal: Loan) => {
    setSelectedDealId(deal.id);
    // The value the deal records, else nothing. R3: a stated LTV is its own
    // sourced figure and never back-solves value — loan ÷ LTV filled Senate
    // Row with $108.0M and returned "In Box" verdicts on it.
    // A component valuation is a Derived value — sized on, labelled with its
    // formula, never called "no property value" (nd-017).
    const reading = propertyValueOf(deal);
    if (reading != null) {
      setPropertyValue(asDollars(reading.value, deal));
      setValueSource(reading.state === 'derived' ? 'derived' : 'record');
      setValueFormula(reading.formula);
    } else {
      setPropertyValue(0);
      setValueSource('none');
      setValueFormula(null);
    }
    // 0, not a guessed figure — NOI is an editable sizer input; an early-stage
    // deal legitimately has none yet, and the field is meant to be filled in.
    const recordedNoi = recordedNoiOf(deal);
    setNoi(recordedNoi != null ? asDollars(recordedNoi, deal) : 0);
    setPropertyType(deal.type ?? '');
    const mkt = MARKETS.find((m) => (deal.location ?? '').includes(m.split(',')[0]));
    setMarket(mkt ?? deal.location ?? '');
  }, []);

  const priorityColor = (p: string) => {
    if (p === 'High') return 'text-red-400';
    if (p === 'Medium') return 'text-amber-400';
    return 'text-stone-400';
  };

  return (
    <div className="flex h-[calc(100vh-4rem)] min-h-0 flex-col gap-2 overflow-hidden p-3">
      <div className="flex flex-shrink-0 items-center gap-2">
        <div className="flex h-6 w-6 items-center justify-center rounded border border-amber-400/20 bg-amber-400/10">
          <Calculator size={12} className="text-amber-300" />
        </div>
        <h1 className="text-sm font-semibold text-stone-100">Screening</h1>
        <p className="truncate text-[10px] text-stone-500">
          Deal sizing, mandate fit, kill-or-advance
        </p>
      </div>

      <div className="grid min-h-0 flex-1 grid-cols-5 gap-2 overflow-hidden">
        {/* ──────────── LEFT: Deal Sizer ──────────── */}
        <div className="col-span-3 flex min-h-0 flex-col overflow-hidden">
          <div className="panel flex min-h-0 flex-1 flex-col overflow-hidden">
            <div className="panel-header flex-shrink-0 !py-1.5">
              <span className="panel-header-title">Deal Sizer</span>
              <span className="font-mono text-[9px] text-stone-500">AUTO-CALC</span>
            </div>
            <div className="panel-body flex min-h-0 flex-1 flex-col gap-2.5 overflow-y-auto !p-2.5">
              <div className="grid grid-cols-4 gap-2">
                <MoneyInput label="Value" value={propertyValue} onValueChange={(v) => { setPropertyValue(v); setValueSource(null); setValueFormula(null) }} />
                <MoneyInput label="NOI" value={noi} onValueChange={setNoi} />
                <label className="space-y-0.5">
                  <span className={fieldLabel}>Type</span>
                  <select
                    className="input input-compact w-full"
                    value={propertyType}
                    onChange={(e) => setPropertyType(e.target.value as AssetClass)}
                  >
                    <option value="">Select…</option>
                    {propertyType && !PROPERTY_TYPES.includes(propertyType) && (
                      <option value={propertyType}>{propertyType}</option>
                    )}
                    {PROPERTY_TYPES.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </label>
                <label className="space-y-0.5">
                  <span className={fieldLabel}>Market</span>
                  <select
                    className="input input-compact w-full"
                    value={market}
                    onChange={(e) => setMarket(e.target.value)}
                  >
                    <option value="">Select…</option>
                    {market && !MARKETS.includes(market) && <option value={market}>{market}</option>}
                    {MARKETS.map((m) => (
                      <option key={m} value={m}>{m}</option>
                    ))}
                  </select>
                </label>
              </div>
              {!selectedDealId && propertyValue === 0 && (
                <p className="text-[10px] text-stone-500">
                  Select a deal from the queue to size it from its record, or enter a value and NOI.
                </p>
              )}
              {valueSource === 'none' && (
                <p className="text-[10px] text-stone-500">This deal records no property value; enter a value to size it.</p>
              )}
              {valueSource === 'derived' && (
                <p className="text-[10px] text-stone-500">
                  <span className="mr-1 rounded-sm bg-violet-500/10 px-1 py-0.5 text-[8px] font-medium uppercase tracking-wider text-violet-300 ring-1 ring-inset ring-violet-500/25">Derived</span>
                  Value derived by the platform{valueFormula ? ` (${valueFormula})` : ''}; no document states it.
                </p>
              )}

              <div className="grid grid-cols-4 gap-1.5">
                {[
                  { label: 'Cap Rate', value: calc.capRate == null ? '—' : pct(calc.capRate) },
                  { label: calc.maxLoan > 0 ? `Max Loan (${pct(calc.maxLtvPct, 0)})` : 'Max Loan', value: calc.maxLoan > 0 ? fmt(calc.maxLoan) : '—' },
                  { label: 'DSCR at Max', value: calc.dscrAtMax == null ? '—' : `${calc.dscrAtMax.toFixed(2)}x` },
                  { label: 'Debt Yield', value: calc.debtYield == null ? '—' : pct(calc.debtYield) },
                ].map((m) => (
                  <div key={m.label} className="metric-cell !px-2 !py-1.5">
                    <div className="metric-cell-label !text-[8px]">{m.label}</div>
                    <div className="metric-cell-value !text-sm">{m.value}</div>
                  </div>
                ))}
              </div>

              <div>
                <div className="mb-1 flex items-baseline justify-between gap-2">
                  <p className={sectionLabel}>Program Fit</p>
                  {creditBox.data && (
                    <p className="font-mono text-[9px] text-stone-500" title={creditBox.data.rules.source}>
                      Credit Box v{creditBox.data.rules.version} · {creditBox.data.rules.status.replace(/_/g, ' ')} · sha {creditBox.data.artifact_sha256.slice(0, 8)}
                    </p>
                  )}
                </div>
                {creditBox.isLoading && <p className="text-[10px] text-stone-500">Loading the credit box…</p>}
                {creditBox.isError && (
                  <p className="text-[10px] text-red-300">
                    The credit box could not be loaded, so no program fit is shown.
                  </p>
                )}
                {rules && (
                  <table className="dense-table !text-[10px]">
                    <thead>
                      <tr>
                        <th className="!py-1">Program</th>
                        <th className="!py-1">Status</th>
                        <th className="!py-1">Detail</th>
                      </tr>
                    </thead>
                    <tbody>
                      {productFits.map((f) => (
                        <tr key={f.programId}>
                          <td className="!py-1 font-medium text-stone-200">{f.name}</td>
                          <td className="!py-1">{fitBadge(f.status)}</td>
                          <td className="!py-1 text-[10px] text-stone-500">{f.reason}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
                <p className="mt-1 text-[9px] text-stone-600">
                  Indicative sizing on the inputs above. The recorded credit-box screen for a deal is on its dossier.
                </p>
              </div>

              {suggested && (
              <section
                aria-label="Suggested structure"
                className="flex shrink-0 flex-col rounded border border-amber-400/15 bg-black/25 px-2 py-1.5"
              >
                <div className="mb-1 flex items-baseline justify-between gap-3">
                  <p className="font-mono text-[9px] uppercase tracking-widest text-stone-400">
                    Suggested Structure · {suggested.product}
                  </p>
                  <p className="font-mono text-[9px] text-stone-500">
                    {suggested.layers.length} layers · {fmt(propertyValue)} capital
                  </p>
                </div>
                <div
                  className={`mb-1 grid gap-1 ${
                    suggested.layers.length === 3 ? 'grid-cols-3' : 'grid-cols-2'
                  }`}
                >
                  {suggested.layers.map((l, i) => {
                    const isEquity = l.label.toLowerCase().includes('equity');
                    return (
                      <div
                        key={l.label}
                        className="rounded border border-amber-400/10 bg-black/40 px-2 py-1"
                      >
                        <div className="mb-0.5 flex items-center gap-1.5">
                          <span
                            className="h-1.5 w-1.5 shrink-0 rounded-sm"
                            style={{ background: chartStackSegmentColor(i, isEquity) }}
                            aria-hidden
                          />
                          <span className="text-[9px] font-medium uppercase tracking-wide text-stone-300">
                            {l.label}
                          </span>
                        </div>
                        <div className="font-mono text-base font-semibold tabular-nums leading-none text-stone-50">
                          {fmt(l.amount)}
                        </div>
                        <div className="mt-1 flex items-center justify-between gap-2 text-[9px]">
                          <span className="text-stone-500">{l.rate || 'Residual'}</span>
                          <span className="font-semibold text-amber-300">{pct(l.pct, 0)}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="flex h-6 w-full overflow-hidden rounded border border-amber-400/15">
                  {suggested.layers.map((l, i) => {
                    const isEquity = l.label.toLowerCase().includes('equity');
                    return (
                      <div
                        key={l.label}
                        className={`flex items-center justify-center text-[9px] font-semibold ${
                          isEquity ? 'text-stone-300' : 'text-stone-900'
                        }`}
                        style={{
                          width: `${l.pct * 100}%`,
                          background: chartStackSegmentColor(i, isEquity),
                        }}
                      >
                        {l.pct >= 0.12 ? `${l.label} ${pct(l.pct, 0)}` : pct(l.pct, 0)}
                      </div>
                    );
                  })}
                </div>
              </section>
              )}

              <div>
                <p className={`${sectionLabel} mb-1`}>Screener Memo</p>
                <ScreenerMemoPanel loanId={selectedDealId} />
              </div>
            </div>
          </div>
        </div>

        {/* ──────────── RIGHT: Screening Queue ──────────── */}
        <div className="col-span-2 flex min-h-0 flex-col overflow-hidden">
          <div className="panel flex min-h-0 flex-1 flex-col overflow-hidden">
            <div className="panel-header flex-shrink-0 !py-1.5">
              <span className="panel-header-title">Screening Queue</span>
              <span className="badge border-amber-400/20 bg-amber-400/10 text-[9px] text-amber-300">
                {queueDeals.length}
              </span>
            </div>
            <div className="panel-body min-h-0 flex-1 space-y-1.5 overflow-y-auto !p-2">
              {queueDeals.map((deal) => {
                const isSelected = selectedDealId === deal.id;
                return (
                  <button
                    key={deal.id}
                    type="button"
                    onClick={() => populateFromDeal(deal)}
                    className={`w-full rounded border px-2 py-1.5 text-left transition-colors ${
                      isSelected
                        ? 'border-amber-400/30 bg-amber-400/[0.06]'
                        : 'border-amber-400/10 bg-black/20 hover:border-amber-400/20'
                    }`}
                  >
                    <div className="mb-0.5 flex items-center justify-between gap-2">
                      <div className="flex min-w-0 items-center gap-1.5">
                        <span className="font-mono text-[9px] text-amber-300">{deal.id}</span>
                        {deal.prioritySuggested ? (
                          <span
                            className="rounded border border-dashed border-stone-500/50 px-1 text-[9px] text-stone-400"
                            title="Suggested — the platform's default priority. No rule or person has set it."
                          >
                            {deal.priority} · suggested
                          </span>
                        ) : (
                          <span className={`text-[9px] font-medium ${priorityColor(deal.priority)}`}>{deal.priority}</span>
                        )}
                      </div>
                      <span className="shrink-0 text-[8px] uppercase tracking-wide text-stone-500">{deal.stage}</span>
                    </div>
                    <div className="mb-0.5 flex items-center gap-1">
                      <Building2 size={10} className="shrink-0 text-stone-500" />
                      <span className="truncate text-[11px] font-medium text-stone-200">{deal.property}</span>
                    </div>
                    <div className="mb-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[9px] text-stone-500">
                      <span className="inline-flex items-center gap-0.5 text-stone-400">
                        <DollarSign size={9} />
                        {/* `loans.loan_amount` is NOT NULL: 0 records that no
                            amount was stated (Cranbury), never a $0 request. */}
                        {deal.loanAmount > 0 ? fmt(asDollars(deal.loanAmount, deal)) : '—'} {loanCapitalProgram(deal) ?? ''}
                      </span>
                      <span>{deal.location}</span>
                      {loanLtvLtc(deal).value != null && (
                        <span className={`font-mono ${(loanLtvLtc(deal).value as number) <= 0.75 ? 'text-emerald-400' : 'text-amber-400'}`}>
                          {ltvLtcText(deal, 0)}
                        </span>
                      )}
                      {deal.dscr != null && (
                        <span className={`font-mono ${deal.dscr >= 1.25 ? 'text-emerald-400' : 'text-amber-400'}`}>
                          DSCR {deal.dscr.toFixed(2)}x
                        </span>
                      )}
                      {deal.debtYield != null && (
                        <span className={`font-mono ${deal.debtYield >= 0.08 ? 'text-emerald-400' : 'text-amber-400'}`}>
                          DY {pct(deal.debtYield, 1)}
                        </span>
                      )}
                    </div>
                    {/* Kill and Advance were controls with no handler (MOD-18).
                        Killing a deal and moving its stage are done from the
                        Pipeline deal inspector, which records the reason and
                        enforces the stage gate; this opens the deal instead. */}
                    <div className="flex items-center gap-1.5">
                      <span
                        className="inline-flex items-center gap-0.5 rounded border border-amber-400/30 px-1.5 py-0.5 text-[9px] font-medium text-amber-300 hover:bg-amber-400/10"
                        onClick={(e) => {
                          e.stopPropagation();
                          useDealDossierStore.getState().openDossier(deal.id, 'overview');
                        }}
                        role="button"
                        title="Open the deal. Kill and stage moves are made in Pipeline → deal inspector."
                      >
                        Open deal <ChevronRight size={10} />
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * The screener memo is the RECORDED screen, printed by the report engine: the credit box's own result
 * against the ruleset it was tested with. It is not re-computed here. This panel used to "generate" a
 * memo client-side: a 1.6-second timer, then a card built from invented arithmetic (expenses as 35% of
 * revenue, debt service at 7.5% of the loan, "1.20x" and "8.5%" floors) and a Proceed / Pass choice no
 * one had made, labelled "Memo ready". Nothing here simulates a result any more.
 */
function ScreenerMemoPanel({ loanId }: {
  /** The screened deal. Without one there is no recorded screen to print. */
  loanId: string | null
}) {
  const [printing, setPrinting] = useState(false)
  const [printError, setPrintError] = useState<string | null>(null)

  /* Print the screener through the report engine. It reproduces the recorded
     credit-box screen against the ruleset it was tested with — it does not
     re-screen, so a deal whose package never established an input prints
     INCOMPLETE rather than passing. */
  const printMemo = async () => {
    if (!loanId) return
    setPrinting(true)
    setPrintError(null)
    try {
      const response = await reportsService.generate({
        template: 'screener',
        filters: { variation: 'sizing_memo', loan_id: loanId },
        format: 'pdf',
      })
      const blob = response.data as Blob
      if (!(blob instanceof Blob) || blob.size === 0) {
        throw new Error('The report engine returned an empty document.')
      }
      const url = URL.createObjectURL(blob)
      const anchor = document.createElement('a')
      anchor.href = url
      anchor.download = `screener-${loanId}.pdf`
      anchor.rel = 'noopener'
      document.body.append(anchor)
      anchor.click()
      anchor.remove()
      window.setTimeout(() => URL.revokeObjectURL(url), 30_000)
    } catch (cause) {
      const detail = (cause as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      setPrintError(
        detail ?? (cause instanceof Error ? cause.message : 'The screener could not be produced.'),
      )
    } finally {
      setPrinting(false)
    }
  }

  return (
    <div className="space-y-1.5 rounded border border-amber-400/15 bg-black/20 px-2 py-1.5">
      <div className="flex flex-wrap items-center gap-1.5">
        <FileText size={10} className="text-amber-400" />
        <span className="text-[10px] font-medium text-stone-300">Recorded screen</span>
        <button
          type="button"
          onClick={() => void printMemo()}
          disabled={printing || !loanId}
          title={loanId ? 'Print the recorded screen for this deal' : 'Select a deal from the screening queue to print its screen'}
          className="btn-primary ml-auto inline-flex items-center gap-1 !px-2 !py-1 text-[10px] disabled:opacity-40"
        >
          <Download size={10} />
          {printing ? 'Printing…' : 'Print screen (PDF)'}
        </button>
      </div>
      <p className="text-[9px] leading-snug text-stone-600">
        The credit box&apos;s recorded result for this deal, against the ruleset it was tested with.
      </p>
      {printError && (
        <div role="alert" className="rounded border border-red-400/30 bg-red-500/10 px-2 py-1 text-[9px] text-red-200">
          {printError}
        </div>
      )}
    </div>
  )
}